import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Try to get current user — cookie or Authorization header will authenticate
    const hasToken = !!localStorage.getItem('lamfon_token');
    if (!hasToken) {
      // No token stored — still try cookie-based auth (might be set by previous session)
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch (err) {
        setUser(null);
      }
    } else {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch (err) {
        console.error('Auth check failed:', err);
        setUser(null);
        localStorage.removeItem('lamfon_token');
      }
    }
    setLoading(false);
  }, []);

  // Auto-subscribe to push notifications after auth
  const autoPushSubscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) {
        await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }
      const existingSub = await (reg || await navigator.serviceWorker.ready).pushManager.getSubscription();
      if (existingSub) return; // Already subscribed

      // Request permission
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return;

      // Get VAPID key and subscribe
      const keyRes = await api.get('/push/vapid-key');
      const vapidKey = keyRes.data.public_key;
      if (!vapidKey) return;

      const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const appServerKey = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i++) appServerKey[i] = rawData.charCodeAt(i);

      const registration = reg || await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey });
      const subJson = sub.toJSON();
      await api.post('/push/subscribe', { endpoint: subJson.endpoint, keys: subJson.keys });
    } catch (err) {
      // Push subscribe skipped
    }
  }, []);

  useEffect(() => {
    // Once user is authenticated, auto-subscribe to push
    if (user?.user_id) {
      autoPushSubscribe();
    }
  }, [user, autoPushSubscribe]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const [mustChangePassword, setMustChangePassword] = useState(false);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { user: userData, token, must_change_password } = res.data;
    try { localStorage.setItem('lamfon_token', token); } catch (e) { /* */ }
    setUser(userData);
    if (must_change_password) setMustChangePassword(true);
    return userData;
  };

  const register = async (data) => {
    try {
      const res = await api.post('/auth/register', data);
      const { user: userData, token } = res.data;
      try { localStorage.setItem('lamfon_token', token); } catch (e) { /* localStorage blocked */ }
      setUser(userData);
      return userData;
    } catch (err) {
      // If user was created but response handling failed, check if we can recover
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already registered')) {
        // User was created in a previous attempt — try logging in instead
        try {
          const loginRes = await api.post('/auth/login', { email: data.email, password: data.password });
          const { user: userData, token } = loginRes.data;
          try { localStorage.setItem('lamfon_token', token); } catch (e) { /* */ }
          setUser(userData);
          return userData;
        } catch (loginErr) {
          throw loginErr;
        }
      }
      throw err;
    }
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const res = await api.get(`/auth/session?session_id=${sessionId}`);
    const { user: userData, token } = res.data;
    localStorage.setItem('lamfon_token', token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) { console.error('Logout error:', err); }
    localStorage.removeItem('lamfon_token');
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, loginWithGoogle,
      processGoogleSession, logout, refreshUser,
      isAuthenticated: !!user, isAdmin: user?.role === 'admin',
      mustChangePassword, setMustChangePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
